import {
  createReportPdf, preloadPdfLogo, drawSectionBanner, drawKpiTiles, drawCallout,
  drawHBar, drawDonutLegend, drawClosingFooter, finalizeFooters, savePdf,
  COLORS, fmtGeneratedAt,
  severityCell, riskCell,
  type DocCtx, type KpiTile,
} from './pdfTemplate';
import autoTable from 'jspdf-autotable';

export interface VulnReportInput {
  customerName: string;
  generatedAt: string;
  tz: string;
  kpi: {
    activeDevices: number;
    totalRecords: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    exploitable: number;
  };
  exposureScore: number | null;
  secureScore: number | null;
  aiBriefMarkdown: string | null;
  patchTuesday: {
    label: string;
    count: number;
    uniqueCves: number;
    devices: number;
  } | null;
  topDevices: { name: string; cveCount: number }[];
  topRemediations: {
    update: string;
    vendor: string;
    devices: number;
    cves: number;
    topSeverity: string;
    avgCvss: number;
  }[];
  topCves: {
    cveId: string;
    severity: string;
    cvssScore: number;
    deviceCount: number;
    exploit: string;
    kev: boolean;
    software: string;
  }[];
  whatIfPlan: {
    items: { update: string; devices: number; cves: number }[];
    devicesRemoved: number;
    cvesRemoved: number;
    criticalRemoved: number;
  } | null;
}

function drawMarkdownBrief(ctx: DocCtx, md: string): void {
  const { pdf } = ctx;
  const lines = md.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { ctx.y += 2; continue; }
    if (line.startsWith('## ')) {
      ctx.y += 2;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.slice(3), 14, ctx.y + 4);
      ctx.y += 7;
      continue;
    }
    if (line.startsWith('# ')) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text(line.slice(2), 14, ctx.y + 4);
      ctx.y += 8;
      continue;
    }
    const bulletMatch = line.match(/^(?:[-*]|\d+\.)\s+(.*)$/);
    const text = bulletMatch ? `\u2022 ${bulletMatch[1]}` : line;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLORS.ink);
    const wrapped = pdf.splitTextToSize(text, 210 - 28);
    for (const w of wrapped) {
      if (ctx.y > 270) { pdf.addPage(); ctx.y = 14; }
      pdf.text(String(w), 14, ctx.y + 4);
      ctx.y += 4.4;
    }
  }
  ctx.y += 2;
}

export async function exportVulnerabilityReportPdf(input: VulnReportInput): Promise<string> {
  await preloadPdfLogo();
  const ctx = createReportPdf({
    title: 'Vulnerability Management Brief',
    customerName: input.customerName,
    periodLabel: 'Live snapshot',
    generatedLabel: fmtGeneratedAt(input.generatedAt, input.tz),
  });

  // KPI tiles
  drawSectionBanner(ctx, 'Executive Overview', 'primary');
  const tiles: KpiTile[] = [
    { value: String(input.kpi.activeDevices), label: 'Active Devices', tone: 'info' },
    { value: String(input.kpi.totalRecords), label: 'Exposure Records', tone: 'default' },
    { value: String(input.kpi.critical), label: 'Critical', tone: 'danger' },
    { value: String(input.kpi.high), label: 'High', tone: 'warning' },
    { value: String(input.kpi.medium), label: 'Medium', tone: 'info' },
    { value: String(input.kpi.low), label: 'Low', tone: 'success' },
    { value: String(input.kpi.exploitable), label: 'Exploitable Pairs', tone: 'danger' },
  ];
  if (input.exposureScore != null) {
    tiles.push({ value: input.exposureScore.toFixed(1), label: 'Exposure Score (lower better)', tone: 'warning' });
  }
  if (input.secureScore != null) {
    tiles.push({ value: `${input.secureScore.toFixed(0)}%`, label: 'Secure Score for Devices', tone: 'success' });
  }
  drawKpiTiles(ctx, tiles);

  // Patch Tuesday callout
  if (input.patchTuesday && input.patchTuesday.count > 0) {
    drawCallout(ctx, {
      tone: 'warning',
      title: `Microsoft Patch Tuesday: ${input.patchTuesday.label}`,
      body:
        `${input.patchTuesday.uniqueCves} unique CVE(s) from the latest Patch Tuesday window currently affect this tenant ` +
        `across ${input.patchTuesday.devices} device(s) (${input.patchTuesday.count} exposure record(s)). ` +
        `These should be prioritized in the next patch cycle.`,
    });
  }

  // AI Brief
  if (input.aiBriefMarkdown) {
    drawSectionBanner(ctx, 'AI Exposure Brief', 'primary');
    drawMarkdownBrief(ctx, input.aiBriefMarkdown);
  }

  // What-if plan
  if (input.whatIfPlan && input.whatIfPlan.items.length > 0) {
    drawSectionBanner(ctx, 'Proposed Patch Plan (What-If)', 'orange');
    drawCallout(ctx, {
      tone: 'success',
      title: 'Projected impact if this plan is executed',
      body:
        `Removes ${input.whatIfPlan.devicesRemoved} device(s) from at-risk inventory, ` +
        `clears ${input.whatIfPlan.cvesRemoved} unique CVE(s) including ${input.whatIfPlan.criticalRemoved} Critical-severity exposure(s).`,
    });
    autoTable(ctx.pdf, {
      startY: ctx.y,
      margin: { left: 14, right: 14 },
      head: [['Remediation', 'Devices Cleared', 'CVEs Cleared']],
      body: input.whatIfPlan.items.map((it) => [it.update, String(it.devices), String(it.cves)]),
      styles: { fontSize: 8.5, cellPadding: 1.5 },
      headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold' },
      didDrawPage: (d) => { ctx.y = d.cursor?.y ?? ctx.y; },
    });
    ctx.y = (ctx.pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 5;
  }

  // Top exposed devices
  if (input.topDevices.length > 0) {
    drawSectionBanner(ctx, 'Top Exposed Devices', 'primary');
    drawHBar(ctx, {
      series: input.topDevices.map((d) => ({
        label: d.name.length > 30 ? d.name.slice(0, 30) + '...' : d.name,
        value: d.cveCount,
        color: COLORS.primary,
      })),
    });
  }

  // Severity distribution
  drawSectionBanner(ctx, 'Severity Distribution', 'primary');
  drawDonutLegend(ctx, {
    slices: [
      { label: 'Critical', value: input.kpi.critical, color: COLORS.danger },
      { label: 'High', value: input.kpi.high, color: COLORS.warning },
      { label: 'Medium', value: input.kpi.medium, color: COLORS.info },
      { label: 'Low', value: input.kpi.low, color: COLORS.success },
    ],
  });

  // Top Remediation Groups
  if (input.topRemediations.length > 0) {
    drawSectionBanner(ctx, 'Top Remediation Groups', 'primary');
    autoTable(ctx.pdf, {
      startY: ctx.y,
      margin: { left: 14, right: 14 },
      head: [['Remediation', 'Vendor', 'Devices', 'CVEs', 'Top Sev', 'Avg CVSS']],
      body: input.topRemediations.map((r) => [
        r.update.length > 50 ? r.update.slice(0, 50) + '...' : r.update,
        r.vendor.length > 18 ? r.vendor.slice(0, 18) : r.vendor,
        String(r.devices),
        String(r.cves),
        severityCell(r.topSeverity),
        r.avgCvss.toFixed(1),
      ]),
      styles: { fontSize: 7.5, cellPadding: 1.2, overflow: 'linebreak' },
      headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 5: { halign: 'center' } },
    });
    ctx.y = (ctx.pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 5;
  }

  // Top CVEs
  if (input.topCves.length > 0) {
    drawSectionBanner(ctx, 'Top CVEs (by device exposure)', 'danger');
    autoTable(ctx.pdf, {
      startY: ctx.y,
      margin: { left: 14, right: 14 },
      head: [['CVE', 'Severity', 'CVSS', 'Devices', 'KEV / Exploit', 'Software']],
      body: input.topCves.map((c) => [
        c.cveId,
        severityCell(c.severity),
        c.cvssScore.toFixed(1),
        String(c.deviceCount),
        riskCell(c.kev ? 'CRITICAL' : c.exploit !== 'NoExploit' ? 'HIGH' : 'LOW'),
        c.software.length > 35 ? c.software.slice(0, 35) + '...' : c.software,
      ]),
      styles: { fontSize: 7.5, cellPadding: 1.2 },
      headStyles: { fillColor: COLORS.danger, textColor: COLORS.white, fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' } },
    });
    ctx.y = (ctx.pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
    ctx.y += 5;
  }

  drawClosingFooter(ctx);
  finalizeFooters(ctx);
  return savePdf(ctx, `vulnerability-brief-${input.customerName.replace(/\s+/g, '-').toLowerCase()}`);
}
